/**
 * CameraService - Utility class for camera operations
 * Handles permission requests, camera initialization, photo capture, and cleanup
 */

class CameraService {
  constructor() {
    this.stream = null;
    this.videoElement = null;
    this.canvas = null;
    this.context = null;
    this.isInitialized = false;
    this.supportedConstraints = null;
  }

  /**
   * Check if camera API is supported
   */
  static isSupported() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  /**
   * Check camera permission status
   */
  async checkPermission() {
    if (!CameraService.isSupported()) {
      return { state: 'unsupported', error: 'Camera API not supported' };
    }

    try {
      // Check if permissions API is available
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' });
        return { state: permission.state };
      }
      
      // Fallback: try to access camera briefly to check permission
      const testStream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 1, height: 1 } 
      });
      testStream.getTracks().forEach(track => track.stop());
      return { state: 'granted' };
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return { state: 'denied', error: 'Camera permission denied' };
      }
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        return { state: 'unavailable', error: 'No camera device found' };
      }
      return { state: 'error', error: error.message };
    }
  }

  /**
   * Request camera permission and initialize
   */
  async requestPermission() {
    if (!CameraService.isSupported()) {
      throw new Error('Camera API not supported in this browser');
    }

    try {
      // Request camera access with optimal constraints
      const constraints = {
        video: {
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          facingMode: 'environment', // Prefer back camera
          aspectRatio: { ideal: 16/9 }
        },
        audio: false
      };

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      return { success: true, stream: this.stream };
    } catch (error) {
      // Try with fallback constraints if initial request fails
      try {
        const fallbackConstraints = {
          video: {
            width: { ideal: 1280, max: 1920 },
            height: { ideal: 720, max: 1080 },
            facingMode: 'user' // Front camera fallback
          },
          audio: false
        };
        
        this.stream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
        return { success: true, stream: this.stream, fallback: true };
      } catch (fallbackError) {
        throw this.handleCameraError(fallbackError);
      }
    }
  }

  /**
   * Initialize camera with video element
   */
  async initialize(videoElement) {
    if (!this.stream) {
      throw new Error('Camera stream not available. Call requestPermission() first.');
    }

    this.videoElement = videoElement;
    this.videoElement.srcObject = this.stream;
    
    // Wait for video to be ready
    return new Promise((resolve, reject) => {
      this.videoElement.onloadedmetadata = () => {
        this.videoElement.play()
          .then(() => {
            this.isInitialized = true;
            this.setupCanvas();
            resolve({
              width: this.videoElement.videoWidth,
              height: this.videoElement.videoHeight
            });
          })
          .catch(reject);
      };
      
      this.videoElement.onerror = () => {
        reject(new Error('Failed to load video stream'));
      };
      
      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Camera initialization timeout'));
      }, 10000);
    });
  }

  /**
   * Setup canvas for photo capture
   */
  setupCanvas() {
    if (!this.videoElement) return;
    
    this.canvas = document.createElement('canvas');
    this.context = this.canvas.getContext('2d');
    
    // Set canvas size to match video
    this.canvas.width = this.videoElement.videoWidth;
    this.canvas.height = this.videoElement.videoHeight;
  }

  /**
   * Capture photo from video stream
   */
  async capturePhoto(options = {}) {
    if (!this.isInitialized || !this.videoElement || !this.canvas) {
      throw new Error('Camera not initialized');
    }

    const {
      quality = 0.8,
      format = 'image/jpeg',
      maxWidth = 1920,
      maxHeight = 1080
    } = options;

    try {
      // Calculate dimensions maintaining aspect ratio
      const videoWidth = this.videoElement.videoWidth;
      const videoHeight = this.videoElement.videoHeight;
      
      let { width, height } = this.calculateDimensions(
        videoWidth, 
        videoHeight, 
        maxWidth, 
        maxHeight
      );

      // Set canvas size with device pixel ratio for better quality
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scaledWidth = width * devicePixelRatio;
      const scaledHeight = height * devicePixelRatio;
      
      this.canvas.width = scaledWidth;
      this.canvas.height = scaledHeight;
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';

      // Draw current video frame to canvas with high quality
      this.context.scale(devicePixelRatio, devicePixelRatio);
      this.context.imageSmoothingEnabled = true;
      this.context.imageSmoothingQuality = 'high';
      this.context.drawImage(this.videoElement, 0, 0, width, height);

      // Convert to blob
      return new Promise((resolve, reject) => {
        this.canvas.toBlob(
          (blob) => {
            if (blob) {
              // Create file object
              const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
              const file = new File(
                [blob], 
                `camera-photo-${timestamp}.jpg`, 
                { type: format }
              );
              
              resolve({
                file,
                blob,
                dataUrl: this.canvas.toDataURL(format, quality),
                width,
                height,
                size: blob.size
              });
            } else {
              reject(new Error('Failed to capture photo'));
            }
          },
          format,
          quality
        );
      });
    } catch (error) {
      throw new Error(`Photo capture failed: ${error.message}`);
    }
  }

  /**
   * Calculate optimal dimensions maintaining aspect ratio
   */
  calculateDimensions(sourceWidth, sourceHeight, maxWidth, maxHeight) {
    const aspectRatio = sourceWidth / sourceHeight;
    
    let width = sourceWidth;
    let height = sourceHeight;
    
    // Scale down if needed
    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    return {
      width: Math.round(width),
      height: Math.round(height)
    };
  }

  /**
   * Switch between front and back camera
   */
  async switchCamera() {
    if (!this.stream) {
      throw new Error('No active camera stream');
    }

    try {
      // Get current facing mode
      const videoTrack = this.stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      const currentFacingMode = settings.facingMode;
      
      // Determine new facing mode
      const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
      
      // Stop current stream
      this.cleanup();
      
      // Request new stream with different facing mode
      const constraints = {
        video: {
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
          facingMode: newFacingMode
        },
        audio: false
      };
      
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Re-initialize if video element exists
      if (this.videoElement) {
        await this.initialize(this.videoElement);
      }
      
      return { success: true, facingMode: newFacingMode };
    } catch (error) {
      throw new Error(`Failed to switch camera: ${error.message}`);
    }
  }

  /**
   * Get available camera devices
   */
  async getAvailableDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices
        .filter(device => device.kind === 'videoinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
          groupId: device.groupId
        }));
    } catch (error) {
      console.warn('Failed to enumerate devices:', error);
      return [];
    }
  }

  /**
   * Handle camera errors with user-friendly messages
   */
  handleCameraError(error) {
    const errorMap = {
      'NotAllowedError': 'Camera permission denied. Please allow camera access and try again.',
      'NotFoundError': 'No camera device found. Please connect a camera and try again.',
      'NotReadableError': 'Camera is already in use by another application.',
      'OverconstrainedError': 'Camera does not support the requested settings.',
      'SecurityError': 'Camera access blocked due to security restrictions.',
      'AbortError': 'Camera access was interrupted.',
      'TypeError': 'Invalid camera configuration.'
    };

    const message = errorMap[error.name] || `Camera error: ${error.message}`;
    const customError = new Error(message);
    customError.name = error.name;
    customError.originalError = error;
    
    return customError;
  }

  /**
   * Get current stream status
   */
  getStatus() {
    return {
      isSupported: CameraService.isSupported(),
      hasStream: !!this.stream,
      isInitialized: this.isInitialized,
      isActive: this.stream && this.stream.active,
      videoElement: !!this.videoElement
    };
  }

  /**
   * Cleanup camera resources
   */
  cleanup() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
      });
      this.stream = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    this.canvas = null;
    this.context = null;
    this.isInitialized = false;
  }

  /**
   * Pause video stream
   */
  pause() {
    if (this.videoElement) {
      this.videoElement.pause();
    }
  }

  /**
   * Resume video stream
   */
  resume() {
    if (this.videoElement) {
      this.videoElement.play();
    }
  }
}

export default CameraService;